// ===============================
// UC Suggestions API Communication types
// ===============================

import { useCallback, useMemo, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	BFFError,
	BFFNetworkError,
	useNavBoundaryContext,
} from '@atlassian/navigation-error-boundary';
import { createExperienceTracker } from '@atlassian/navigation-perf-utils';
import type { UniversalCreateSourceProduct } from '@atlassian/universal-create-common';

import { RequestStatus, UC_SUGGESTIONS_REQUEST_STATUS_KEY } from '../../constants';
import { useBFFContext } from '../context';
import { fetchJSON } from '../utils/request';

import { useGenericCache } from './useGenericCache';
import { useStatus } from './useStatus';
import { useURLBuilder } from './useURLBuilder';

export interface SuggestionItemResponseBase {
	/**
	 * The ATI of the object type
	 */
	ati: string;
	/**
	 * The icon name from @atlaskit/icon/core/{name}
	 */
	icon?: string;
	/**
	 * The name of the object type
	 */
	name: string;
	/**
	 * The product name where the object type belongs to
	 */
	product: UniversalCreateSourceProduct;
}

export interface IntegrationItemResponse extends SuggestionItemResponseBase {
	actionType: string;
	type: 'integration';
}

export interface IframeItemResponse extends SuggestionItemResponseBase {
	src: string;
	type: 'iframe';
}

export interface LinkItemResponse extends SuggestionItemResponseBase {
	hostname: string;
	href: string;
	options?: Record<string, string | number | boolean>;
	sessionSyncUrl?: string;
	target: '_self' | '_blank';
	type: 'link';
}

export interface PlatformItemResponse extends SuggestionItemResponseBase {
	type: 'platform';
}

export type SuggestionItemResponse =
	| IntegrationItemResponse
	| IframeItemResponse
	| LinkItemResponse
	| PlatformItemResponse;

export interface SuggestionsAPIResponse {
	isPartial?: boolean;
	items: SuggestionItemResponse[];
}

interface UseUCSuggestionsProps {
	cloudId: string;
	excludeAtis?: string[];
	locale?: string;
	nativeAtis?: string[];
	orgId: string;
	preferredAtis?: string[];
	product: string;
	subProduct?: string;
	userId: string;
}

export interface UseUCSuggestionsReturn {
	data: SuggestionsAPIResponse | undefined;
	fetchUCSuggestions: () => Promise<SuggestionsAPIResponse | undefined>;
	hasError: boolean;
	isLoading: boolean;
	status: RequestStatus | null;
}

/**
 * Hook for fetching UC suggestions with smart caching
 *
 * @deprecated This V1 hook is deprecated and will be removed in a future release.
 * Use the V2 UC menu items hook instead.
 */
export function useUCSuggestions({
	cloudId,
	orgId,
	product,
	userId,
	locale,
	subProduct,
	nativeAtis,
	excludeAtis,
	preferredAtis,
}: UseUCSuggestionsProps): UseUCSuggestionsReturn {
	const isV1Disabled = fg('navx_uc_v1_suggestions_bff_disabled');

	const { value, currentLoadingRequests } = useBFFContext();
	const boundaryConfig = useMemo(
		() => ({
			location: 'useUCSuggestions' as const,
			disableSentry: false,
			disableAnalytics: true,
			sentryConfig: { product },
			interactionId: 'universalCreatePrefetch' as const,
			tags: {
				component: 'universal-create' as const,
				subProduct: subProduct,
			},
		}),
		[product, subProduct],
	);
	const { handleError } = useNavBoundaryContext(boundaryConfig);
	const { setStatus } = useStatus();
	const [data, setData] = useState<SuggestionsAPIResponse | undefined>(undefined);
	const [hasError, setHasError] = useState(false);

	const { buildUCRecommendationURL } = useURLBuilder({ cloudId });
	const { cachedObject, setCachedObject } = useGenericCache<SuggestionsAPIResponse>({
		cloudId,
		productType: product,
		orgId,
		accountId: userId,
		subProduct,
		locale,
		nativeAtis,
		excludeAtis,
		preferredAtis,
	});

	const requestIdRef = useRef(0);

	// We need to set the data from the cache on mount (if applicable)
	// because setting it as the initial state value causes it to
	// return undefined in component usages and not be fetched from the network when expected
	if (cachedObject && !data) {
		setData(cachedObject);
		setHasError(false);
	}

	const fetchUCSuggestions = useCallback(async (): Promise<SuggestionsAPIResponse | undefined> => {
		// V1 deprecation: skip BE call when gate is enabled
		if (isV1Disabled) {
			return;
		}

		// Early exit: filter out invalid param requests when required identifiers are missing
		if (!cloudId || !orgId) {
			return;
		}

		// Return cached data if available (already locale-scoped via cache key)
		if (cachedObject) {
			setStatus(RequestStatus.COMPLETE, UC_SUGGESTIONS_REQUEST_STATUS_KEY);
			return cachedObject;
		}

		// Increment request id for race protection
		requestIdRef.current += 1;
		const scopedId = requestIdRef.current;

		// Create and start experience tracker only when actually making request
		const experience = createExperienceTracker('universalCreatePrefetch');

		experience?.start();

		try {
			const url = buildUCRecommendationURL({
				product,
				orgId,
				userId,
				locale,
				subProduct,
				nativeAtis,
				excludeAtis,
				preferredAtis,
			});
			setStatus(RequestStatus.LOADING, UC_SUGGESTIONS_REQUEST_STATUS_KEY);

			const response = await fetchJSON<SuggestionsAPIResponse>(url, {});

			// Ignore if this response is for a stale request
			if (scopedId !== requestIdRef.current) {
				return;
			}

			if (response.status === RequestStatus.ERROR) {
				experience?.failure();
				setHasError(true);
				setData(undefined);
				setStatus(RequestStatus.ERROR, UC_SUGGESTIONS_REQUEST_STATUS_KEY);

				handleError(
					new BFFNetworkError('Failed to fetch universal create suggestions', {
						url,
						status: response.status,
						originalError: response.error,
					}),
				);

				return;
			}

			if (response.status === RequestStatus.COMPLETE && response.data) {
				experience?.success();
				setData(response.data);
				setHasError(false);

				// Only cache if the data is not partial
				if (!response.data.isPartial) {
					setCachedObject(response.data);
				}

				setStatus(RequestStatus.COMPLETE, UC_SUGGESTIONS_REQUEST_STATUS_KEY);

				return response.data;
			}
		} catch (error) {
			experience?.failure();

			if (scopedId === requestIdRef.current) {
				setHasError(true);
				setData(undefined);
				setStatus(RequestStatus.ERROR, UC_SUGGESTIONS_REQUEST_STATUS_KEY);
			}

			handleError(
				new BFFError('Error occurred during fetchUCSuggestions', {
					status: RequestStatus.ERROR,
					originalError: error,
				}),
			);
		}

		return;
	}, [
		isV1Disabled,
		cloudId,
		orgId,
		cachedObject,
		setStatus,
		buildUCRecommendationURL,
		product,
		userId,
		locale,
		subProduct,
		nativeAtis,
		excludeAtis,
		preferredAtis,
		handleError,
		setCachedObject,
	]);

	const isLoading = useMemo(() => {
		// We check if the current loading requests include UC suggestions rather than the BFF status directly
		// to ensure that it only returns true for UC suggestions and not other BFF requests.
		const isBffLoadingOurStuff =
			currentLoadingRequests?.includes(UC_SUGGESTIONS_REQUEST_STATUS_KEY) ?? false;

		// That said, if the BFF is in loading state but data has been returned, we want to wait for things to complete
		// before running things like onResultChange, so that we don't do those things an unnecessary extra time
		// during the brief moment when data is present but loading is still true, or an error has occurred and loading is still true.
		const hasSuggestionDataBeenReturned = data !== undefined || hasError;

		return isBffLoadingOurStuff && !hasSuggestionDataBeenReturned;
	}, [currentLoadingRequests, data, hasError]);

	if (isV1Disabled) {
		return {
			data: undefined,
			isLoading: false,
			hasError: false,
			status: null,
			fetchUCSuggestions,
		};
	}

	return {
		data,
		isLoading,
		hasError,
		status: value.status,
		fetchUCSuggestions,
	};
}
